import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import MailchimpSubscribe from "react-mailchimp-subscribe";
import CustomForm from "../utils/CustomForm";
import { Mail } from "@mui/icons-material";

export default function Newsletter() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submissionSuccess, setSubmissionSuccess] = React.useState(null);

  // SEO Data
  const seoData = useMemo(
    () => ({
      title:
        "Subscribe to Our Newsletter | Digestive-Friendly Recipes | GentleGourmet",
      description:
        "Get weekly digestive-friendly recipes, cooking tips for GERD, IBS and gastritis, plus health insights delivered to your inbox. Join our community of health-conscious foodies.",
      keywords:
        "newsletter, digestive recipes, GERD newsletter, IBS recipes email, gastritis diet tips, healthy eating tips, recipe updates, acid reflux friendly",
      canonicalUrl: "https://gentlegourmet.app/newsletter",
      ogImage: "https://gentlegourmet.app/images/og-newsletter.jpg",
    }),
    []
  );

  // JSON-LD Structured Data
  const jsonLdWebPage = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Newsletter Signup - GentleGourmet",
      description: seoData.description,
      url: seoData.canonicalUrl,
      isPartOf: {
        "@type": "WebSite",
        name: "GentleGourmet",
        url: "https://gentlegourmet.app",
      },
      potentialAction: {
        "@type": "SubscribeAction",
        target: seoData.canonicalUrl,
        name: "Subscribe to Newsletter",
      },
    }),
    [seoData]
  );

  const jsonLdBreadcrumbs = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://gentlegourmet.app/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Newsletter",
          item: seoData.canonicalUrl,
        },
      ],
    }),
    [seoData]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(event.target);
    const url = event.target.action; // URL from the action attribute of the form

    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      if (response.ok) {
        // Successful submission
        setSubmissionSuccess(true);
        navigate("/success_page?status=success");
      } else {
        // Handle errors, for example, show a message to the user
        setSubmissionSuccess(false);
        navigate("/current_page?status=failure");
      }
    } catch (error) {
      // Handle network errors
      setSubmissionSuccess(false);
      navigate("/current_page?status=failure");
    } finally {
      setIsSubmitting(false);
    }
  };

  const mailchimpUrl =
    "https://app.us12.list-manage.com/subscribe/post?u=d2efa1212ff18f337c2aa30ce&amp;id=195eb4aefa";
  return (
    <>
      {/* SEO Head Tags */}
      <Helmet>
        <html lang="en" />
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonicalUrl} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonicalUrl} />
        <meta property="og:image" content={seoData.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="GentleGourmet" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.ogImage} />
        <script type="application/ld+json">
          {JSON.stringify(jsonLdWebPage)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(jsonLdBreadcrumbs)}
        </script>
      </Helmet>

      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-3xl mx-auto p-6 w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex flex-col items-center space-y-6">
              {/* Header */}
              <div className="text-center space-y-4">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                  Receive Acid-Reflux Friendly Recipes
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg">
                  Sign up to get our best GERD, acid-reflux, and gastritis
                  friendly recipes, cooking tips, must-have kitchen products and
                  news — delivered straight to your inbox.
                </p>
              </div>

              {/* Community Banner */}
              <div className="bg-purple-50 w-full p-6 rounded-xl text-center">
                <p className="text-gray-700 text-base md:text-lg">
                  Join our growing community of over 100+ people passionate
                  about gastritis-friendly recipes!{" "}
                  <button
                    onClick={() => navigate("/sign_up")}
                    className="text-purple-600 hover:text-purple-700 font-medium underline decoration-2 decoration-purple-300 hover:decoration-purple-500 transition-colors duration-200"
                  >
                    Sign up to our platform
                  </button>
                </p>
              </div>

              {/* Mailchimp Form */}
              <div className="w-full">
                <MailchimpSubscribe
                  url={mailchimpUrl}
                  render={({ subscribe, status, message }) => (
                    <CustomForm
                      status={status}
                      formMessage={message}
                      onValidated={(formData) => subscribe(formData)}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
